import { useQuery } from '@tanstack/react-query';
import { API } from '../utils/api.utils';
import api from '../utils/api.utils';

interface University {
  name: string;
  id: number;
}

interface Supplement {
  id: number;
  supplement_number: number;
  min_number_required: number;
  name: string | null;
  description: string | null;
  status: string;
  ai_specific_advice: string | null;
}

interface ApplicationType {
  id: number;
  name: string;
  description: string | null;
  type: string;
  ai_specific_advice: string | null;
}

interface UniversityData {
  university_id: number;
  university_name: string;
  days_remaining: number;
  supplements: Supplement[];
  application_types: ApplicationType[];
}

interface ApplicationRequirements {
  university_id: number;
  uni_app_type_mapping: {
    [key: string]: boolean;
  };
  min_supplements_required: number;
}

const fetchUniversities = async (): Promise<University[]> => {
  const response = await api.get(API.UNIVERSITIES_ALL);
  return response.data;
};

const fetchUniversityData = async (universityId: number): Promise<UniversityData> => {
  const response = await api.get(`${API.UNIVERSITIES}/${universityId}/supplements-and-applications`);
  return response.data;
};

const fetchApplicationRequirements = async (universityId: number): Promise<ApplicationRequirements> => {
  const response = await api.get(`${API.UNIVERSITIES}/${universityId}/application-types-acceptance`);
  return response.data;
};

export const useUniversities = () => {
  return useQuery({
    queryKey: ['universities'],
    queryFn: fetchUniversities,
    staleTime: 30 * 60 * 1000, // 30 minutes - data stays fresh for 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour - cache persists for 1 hour
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnMount: false, // Don't refetch when component mounts if data exists
    refetchOnReconnect: false, // Don't refetch when reconnecting to network
  });
};

export const useUniversityData = (universityId: number) => {
  return useQuery({
    queryKey: ['university-data', universityId],
    queryFn: () => fetchUniversityData(universityId),
    enabled: !!universityId,
    staleTime: 30 * 60 * 1000, // 30 minutes - data stays fresh for 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour - cache persists for 1 hour
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnMount: false, // Don't refetch when component mounts if data exists
    refetchOnReconnect: false, // Don't refetch when reconnecting to network
  });
};

export const useApplicationRequirements = (universityId: number) => {
  return useQuery({
    queryKey: ['application-requirements', universityId],
    queryFn: () => fetchApplicationRequirements(universityId),
    enabled: !!universityId,
    staleTime: 30 * 60 * 1000, // 30 minutes - data stays fresh for 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour - cache persists for 1 hour
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

export type { University, Supplement, ApplicationType, UniversityData, ApplicationRequirements };
